import {Canvas} from '@postermywall/fabricjs-2';
import type {Page} from './page.class';
import Emitter from '@/services/event-emitter';
import {AlignmentGuides} from '@PosterWhiteboard/page/alignment-guides/alignment-guides.class';
import type {ResizeSnapStep} from '@PosterWhiteboard/page/alignment-guides/alignment-guides.types';

/** Owns the lifecycle of a page's {@link AlignmentGuides}: the user preference, and suspension by on-canvas modes. */
export class RenderAlignmentGuidelines {
  public page: Page;
  private alignmentGuides: AlignmentGuides | null = null;
  private isDisposed = false;
  /** While suspended the guides stay down whatever the user preference says; see {@link suspend}. */
  private isSuspended = false;
  private readonly boundOnAreAlignmentGuidesEnabledUpdated: () => void;

  constructor(page: Page) {
    this.page = page;
    this.boundOnAreAlignmentGuidesEnabledUpdated = this.onAreAlignmentGuidesEnabledUpdated.bind(this);
    Emitter.on('userPreferences:areAlignmentGuidesEnabledUpdated', this.boundOnAreAlignmentGuidesEnabledUpdated);
    if (window.PMW.redux.store.getState().userPreferences.areAlignmentGuidesEnabled) {
      this.enableAligningGuidelines();
    }
  }

  public dispose(): void {
    this.isDisposed = true;
    Emitter.off('userPreferences:areAlignmentGuidesEnabledUpdated', this.boundOnAreAlignmentGuidesEnabledUpdated);
    this.disableAligningGuidelines();
  }

  private onAreAlignmentGuidesEnabledUpdated(): void {
    if (this.isDisposed) {
      return;
    }
    if (window.PMW.redux.store.getState().userPreferences.areAlignmentGuidesEnabled) {
      this.enableAligningGuidelines();
    } else {
      this.disableAligningGuidelines();
    }
  }

  /**
   * Takes every guide down until {@link resume}: on-canvas modes such as image cropping move objects
   * that are not items, and the centring, edge and alignment lines would read as guidance for a frame
   * that has nothing to align with.
   */
  public suspend(): void {
    this.isSuspended = true;
    this.disableAligningGuidelines();
  }

  /** Lifts a suspension and brings the guides back if the user has them on. */
  public resume(): void {
    this.isSuspended = false;
    this.onAreAlignmentGuidesEnabledUpdated();
  }

  public enableAligningGuidelines(): void {
    if (this.isDisposed || this.isSuspended || this.alignmentGuides) {
      return;
    }
    if (this.page.fabricCanvas instanceof Canvas) {
      this.alignmentGuides = new AlignmentGuides(this.page, this.page.fabricCanvas);
    }
  }

  public disableAligningGuidelines(): void {
    this.alignmentGuides?.dispose();
    this.alignmentGuides = null;
  }

  /** See {@link AlignmentGuides.snapResize}; a no-op while the guides are off or suspended. */
  public snapResize(step: ResizeSnapStep): void {
    this.alignmentGuides?.snapResize(step);
  }

  /** See {@link AlignmentGuides.endResize}. */
  public endResize(): void {
    this.alignmentGuides?.endResize();
  }
}
